import { IExitModel } from './IProductData';
import { IScript } from './IScript';
import { IPins } from './IPins';
import { enFrequencyType } from 'enums/enFrequencyType';
import { enProductType } from 'enums/enProductType';
import { enPaymentType } from 'enums/enPaymentType';
import { enPaymentMethod } from 'enums/enPaymentMethod';
import { ITickets, IReservationData } from './ITickets';
import { INext } from './INext';
import { enShippingType } from 'enums/enShippingType';

export interface IProductTemplateMediaConfig {
  readonly background?: IBackgroundTemplateMediaConfig;
  readonly bannerCenter: string;
  readonly bannerTop: string;
  readonly bannerRight: string;
  readonly bannerYoutube: string;
}
export interface IBackgroundTemplateMediaConfig {
  readonly image: string;
  readonly color: number;
}
export interface IProductTemplateConfig {
  readonly id: number;
  readonly media: IProductTemplateMediaConfig;
  readonly styles: any;
  readonly inputStyle: 'normal' | 'material';
}

export enum enCurrency {
  BRL = 'BRL',
  USD = 'USD'
}

export enum enLang {
  EN_US = 'enus',
  PT_BR = 'ptbr',
  PT_PT = 'ptpt'
}

export enum enCountry {
  BR = 'BR',
  US = 'US'
}

export interface IPixel {
  readonly userId?: string;
  readonly campaignKey?: string;
  readonly contentId?: string;
  readonly campaign?: string;
}

export interface IExitModelButton {
  readonly enabled?: boolean;
  readonly theme?: IExitModelButtonTheme;
}

export interface IExitModelButtonTheme {
  readonly text?: string;
  readonly textColor?: string;
  readonly textSize?: number;
  readonly backgroundColor?: string;
  readonly backgroundColorHover?: string;
}

export interface IExitModelTimerTheme {
  readonly title?: string;
  readonly titleColor?: string;
  readonly titleSize?: number;
  readonly backgroundColor?: string;
}

export interface IExitModelMedia {
  readonly video?: string;
  readonly audio?: string;
  readonly image?: string;
}

export interface IExitModelTimer {
  readonly time?: number;
  readonly enabled?: boolean;
  readonly theme?: IExitModelTimerTheme;
}

export interface IExitModel {
  readonly coupon?: string;
  readonly media: IExitModelMedia;
  readonly timer?: IExitModelTimer;
  readonly button?: IExitModelButton;
}

export interface IProductConfig {
  readonly affiliateId: number;
  readonly askAddressAfterPayment: boolean;
  readonly askAddressBeforePayment: boolean;
  readonly askCVV: boolean;
  readonly availableCurrencies: enCurrency[];
  readonly currency: enCurrency;
  readonly ead: boolean;
  readonly enableBankslip: boolean;
  readonly enableCreditcard: boolean;
  readonly enablePaypal: boolean;
  readonly exitModal?: IExitModel;
  readonly foreignEnabled: boolean;
  readonly hasCoupon: boolean;
  readonly hideQuantity: boolean;
  readonly isLaunch: boolean;
  readonly isOneClick: boolean;
  readonly lang: enLang;
  readonly logo: string;
  readonly maxInstallments: number;
  readonly multipleCreditCards: boolean;
  readonly mustEstimate: boolean;
  readonly package: boolean;
  readonly paymentType: enPaymentType;
  readonly pixel: IPixel;
  readonly producerId: number;
  readonly productType: enProductType;
  readonly purchaseLimit?: number;
  readonly scripts: IScript[];
  readonly template: IProductTemplateConfig;
  readonly shippingType?: enShippingType;
  readonly showCardDocument?: boolean;
  readonly askDocument: boolean;
}

export interface IProduct {
  readonly id: number;
  readonly title: string;
  readonly description: string;
  readonly itemId: number;
  readonly quantity: number;
  readonly totalPrice: number;
  readonly unitPrice: number;
  readonly tax: number;
  readonly taxedPrice: number;
  readonly realPrice: number;
}

export interface IInstallment {
  readonly installment: number;
  readonly value: number;
}

export interface ICoupon {
  readonly code: string;
  readonly value: number;
}

export interface ICouponData {
  readonly coupon: ICoupon;
  readonly price: number;
  readonly realPrice: number;
  readonly taxedPrice: number;
  readonly cartPrice: number;
  readonly tax: number;
  readonly installments: IInstallment[];
  readonly products: IProduct[];
}

export interface IShippingData {
  readonly hash: string;
  readonly products: IProduct[];
  readonly price: number;
  readonly realPrice: number;
  readonly tax: number;
  readonly realTax: number;
  readonly taxedPrice: number;
  readonly cartPrice: number;
  readonly installments: IInstallment[];
  readonly shippingType: number;
  readonly shippingItems: IShipping[];
  readonly reservation: IReservationData;
}

export interface IShipping {
  readonly title: string;
  readonly value: number;
  readonly deadline: number;
  readonly code: string;
  readonly totalValue: number;
  readonly installments: IInstallment[];
  readonly price: number;
  readonly realPrice: number;
  readonly tax: number;
  readonly taxedPrice: number;
  readonly cartPrice: number;
}

export interface IUser {
  readonly id?: number;
  readonly street?: string;
  readonly cityId?: number;
  readonly city?: string;
  readonly complement?: string;
  readonly countryId?: number;
  readonly district?: string;
  readonly number?: number;
  readonly province?: string;
  readonly phone?: string;
  readonly document?: string;
  readonly email?: string;
  readonly name?: string;
  readonly personType?: string;
  readonly postalCode?: string;
  readonly creditCard?: ICreditCard;
  readonly lock?: boolean;
}

export interface ICreditCard {
  readonly id: number;
  readonly brand: string;
  readonly identifier: string;
  readonly secret: string;
  readonly month: number;
  readonly year: number;
  readonly creationDate: string;
  readonly updatedDate: string;
}

export interface ISubscription {
  readonly frequency?: number;
  readonly frequencyType?: enFrequencyType;
  readonly hasTax?: boolean;
  readonly tax?: number;
  readonly taxTax?: number;
  readonly hasTrial?: boolean;
  readonly trial?: number;
  readonly hasTrialTax?: boolean;
  readonly trialTax?: number;
  readonly price?: number;
  readonly priceTax?: number;
}

export interface IOrderBump {
  readonly contentId: string;
  readonly title: string;
  readonly logo: string;
  readonly price: number;
}

export interface IContent {
  readonly id: number;
  readonly title: string;
  readonly description: string;
  readonly producerName: string;
  readonly producerEmail: string;
}

export interface IProductData {
  readonly contentId: string;
  originalContentId: string;
  readonly content: IContent;
  readonly config: IProductConfig;
  readonly coupon: ICoupon;
  readonly feeInstallment: number;
  readonly goofyId: string;
  readonly hasValidCoupons: boolean;
  readonly installmentSelected?: number;
  readonly installments: IInstallment[];
  readonly price: number;
  readonly products: IProduct[];
  readonly realPrice: number;
  readonly realTax: number;
  readonly subscription?: ISubscription;
  readonly tax: number;
  readonly taxedPrice: number;
  readonly cartPrice: number;
  readonly transactionKey: string;
  readonly upsell?: enPaymentMethod;
  readonly user?: IUser;
  readonly pins: IPins;
  readonly tickets: ITickets;
  readonly next?: INext;
  readonly orderBump?: IOrderBump[];

  isCached: boolean;
  hash?: string;
  numberInstallments: number;
}
